import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Centinela caído (era IV, docs/CRIATURAS.md «Bastión de la Vigilia»): a knight of the old garrison of the
 * Order, the night the gate fell, as the titan dreams him now. Half again a hero's height, in tarnished dark
 * plate dented and scored, a crimson tabard of the Order rotted to rags with its pale diamond still on the
 * breast, a great helm with nothing inside but two eyes of pale blue flame and a cold flame for a crest, and a
 * tower shield carried square before him (his trait: it stops anything shot at him from the front) with the
 * Order's diamond on it, cracked, the cold fire showing through the cracks. A broken longsword hangs from his
 * right hand.
 *
 * Modelled in his guard: the shield arm bent with the shield before his chest, the sword arm hanging. No rest
 * rotations (the clips are offsets from this pose). Bones: body (hips), chest, head, the tabard's front and back
 * flaps, armL/foreL/shield (the shield arm), armR/foreR/sword, legs (thigh, shin). Faces +Z, `R` limbs on +X.
 */

/** Model height (the crest's flame on top of the helm). */
export const SENTINEL_HEIGHT = 2.15;

export const sentinel = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 401);
  const { g, rng } = c;
  const k4 = kit(rng);

  // Tarnished plate (cool, light enough to catch the key light from the table camera), its darker lames,
  // black mail, the Order's crimson rotted dark, the pale steel of the emblem and the blade.
  const plate = metal('#5a616b', { roughness: 0.5, metalness: 0.45, bumpScale: 0.025 });
  const plateD = metal('#33373e', { roughness: 0.6, metalness: 0.45, bumpScale: 0.03 });
  const mail = std('#1d1f24', { roughness: 0.7, metalness: 0.3, bumpScale: 0.06 });
  const tabard = cloth('#6a161b', { side: THREE.DoubleSide });
  const pale = metal('#c3cad4', { roughness: 0.35, metalness: 0.5 });
  const leatherD = std('#1e1a1a', { roughness: 0.8 });
  // The shield's face: the Order's crimson, chipped and blackened.
  const paint = std('#5e1419', { roughness: 0.75, bumpScale: 0.05 });
  // The cold flame: eyes, crest and the fire in the shield's cracks (the one self-lit material).
  const cold = emis('#eef6ff', '#8cc8ff', 2.4);
  const coldF = emis('#dcefff', '#7fbfff', 1.8, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#1a1e28', 0.5);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: cuisses, knee cops with their wings, greaves and pointed sabatons ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.16, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.16, 1.02, 0), V(k * 0.17, 0.58, 0.02), 0.13, 0.1, plate, 7);
    const shinB = bone(hip, V(k * 0.17, 0.56, 0.02), `shin${s}`);
    const lo = shinB.abs;
    // Knee cop: a dome with a fan-shaped wing on the outside.
    const cop = k4.swell(lo, plate, k * 0.17, 0.56, 0.08, 0.085, 0.08, 0.07, 7, 4);
    cop.rotation.x = -0.3;
    const wing = M(new THREE.CylinderGeometry(0.07, 0.07, 0.015, 6, 1, false, 0, Math.PI), plateD, lo);
    wing.position.set(k * 0.24, 0.56, 0.05);
    wing.rotation.set(0, 0, k * Math.PI / 2);
    limb(lo, V(k * 0.17, 0.54, 0.02), V(k * 0.17, 0.13, 0.0), 0.1, 0.078, plate, 7);
    // Sabaton: a box of lames with a pointed toe.
    const sab = M(new THREE.BoxGeometry(0.17, 0.1, 0.26), plateD, lo);
    sab.position.set(k * 0.17, 0.05, 0.05);
    const toe = M(new THREE.ConeGeometry(0.075, 0.14, 4), plateD, lo);
    toe.position.set(k * 0.17, 0.04, 0.22);
    toe.rotation.set(Math.PI / 2, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.55);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: a skirt of mail under plate faulds, the sword belt, the tabard's flaps ---------- */
  const bodyB = bone(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.27, 0.33, 0.3, 9, 1, true), mail, body).position.set(0, 0.86, 0);
  const fauld = M(new THREE.CylinderGeometry(0.26, 0.3, 0.2, 9), plateD, body);
  fauld.position.set(0, 1.04, 0);
  const belt = M(new THREE.TorusGeometry(0.27, 0.025, 3, 12), leatherD, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.12, 0);
  belt.scale.set(1, 0.8, 1);
  // The tabard's flaps hang from the belt, front and back, rotted ragged.
  const flapF = bone(body, V(0, 1.1, 0.22), 'flapF');
  k4.panel(flapF.abs, V(0, 1.11, 0.25), 0.34, 0.72, 0.02, 1, tabard, 0.45);
  const flapB = bone(body, V(0, 1.1, -0.22), 'flapB');
  const back = k4.panel(flapB.abs, V(0, 1.11, -0.25), 0.38, 0.8, 0.02, -1, tabard, 0.4);
  back.rotation.y = 0;
  // The Order's diamond on the back flap (seen from behind, as the line marches away).
  const dB = M(new THREE.BoxGeometry(0.11, 0.11, 0.012), pale, flapB.abs);
  dB.position.set(0, 0.84, -0.262);
  dB.rotation.z = Math.PI / 4;

  /* ---------- chest: breastplate with a ridge, the tabard over it, the gorget, great pauldrons ---------- */
  const chestB = bone(body, V(0, 1.16, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, plate, 0, 1.42, 0.0, 0.31, 0.3, 0.21, 9, 6);
  k4.swell(chest, plateD, 0, 1.22, 0.01, 0.27, 0.13, 0.19, 8, 4);
  // The tabard over the breast: a curved panel, short and torn.
  const tb = k4.panel(chest, V(0, 1.6, 0.22), 0.38, 0.5, 0.08, 1, tabard, 0.15);
  tb.rotation.x = -0.12;
  const dF = M(new THREE.BoxGeometry(0.1, 0.1, 0.012), pale, chest);
  dF.position.set(0.0, 1.44, 0.235);
  dF.rotation.z = Math.PI / 4;
  // The back: a plain backplate showing above the flap, the tabard's back over it.
  const tbb = k4.panel(chest, V(0, 1.6, -0.17), 0.4, 0.52, 0.05, -1, tabard, 0.15);
  tbb.rotation.x = 0.1;
  M(new THREE.CylinderGeometry(0.12, 0.15, 0.1, 8), plateD, chest).position.set(0, 1.66, 0);
  for (const k of [-1, 1]) {
    // Layered pauldrons: a dome over two lames; the shield side wears a raised flange to guard the neck.
    const pd = G(chest, k * 0.36, 1.63, 0);
    pd.rotation.z = -k * 0.35;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), plate, pd);
    dome.scale.set(0.2, 0.15, 0.2);
    for (let i = 1; i <= 2; i++) {
      const lame = M(new THREE.CylinderGeometry(0.2 - i * 0.012, 0.21 - i * 0.01, 0.06, 8, 1, true), i === 1 ? plateD : plate, pd);
      lame.position.y = -0.04 - i * 0.055;
    }
    if (k < 0) {
      const fl = M(new THREE.BoxGeometry(0.03, 0.17, 0.3), plate, pd);
      fl.position.set(0.1, 0.13, 0);
      fl.rotation.z = -0.35;
    }
  }

  /* ---------- head: a great helm, the visor's slit with the eyes of cold flame, the flame crest ---------- */
  const headB = bone(chest, V(0, 1.7, 0.01), 'head');
  const head = headB.abs;
  M(new THREE.CylinderGeometry(0.135, 0.145, 0.27, 9), plate, head).position.set(0, 1.84, 0.01);
  const crown = M(new THREE.ConeGeometry(0.137, 0.1, 9), plate, head);
  crown.position.set(0, 2.02, 0.01);
  // A ridge down the face, the slit across it, and the breaths.
  const ridge = M(new THREE.BoxGeometry(0.025, 0.24, 0.03), plateD, head);
  ridge.position.set(0, 1.84, 0.15);
  const slit = M(new THREE.BoxGeometry(0.22, 0.03, 0.03), leatherD, head);
  slit.position.set(0, 1.88, 0.136);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.024, 6, 4), cold, head).position.set(k * 0.05, 1.88, 0.14);
    glowSprite(head, '#cfe6ff', 0.2, V(k * 0.05, 1.88, 0.17), 0.95);
    for (let i = 0; i < 3; i++) {
      const h = M(new THREE.BoxGeometry(0.012, 0.03, 0.02), leatherD, head);
      h.position.set(k * (0.04 + i * 0.025), 1.78, 0.138);
    }
  }
  // A dent and a gash in the helm.
  const dent = M(new THREE.BoxGeometry(0.05, 0.012, 0.02), plateD, head);
  dent.position.set(0.08, 1.95, 0.11);
  dent.rotation.set(0, 0.6, 0.5);
  // The crest: a tongue of cold flame streaming back off the crown (it flickers in the frames).
  const crest = G(head, 0, 2.06, 0.0);
  crest.rotation.x = -0.35;
  const flames: THREE.Mesh[] = [];
  for (const [x, z, s, h] of [[0, 0, 1, 0.34], [0.02, -0.05, 0.75, 0.26], [-0.025, 0.04, 0.6, 0.2]] as const) {
    const f = M(new THREE.ConeGeometry(0.055 * s, h, 6), coldF, crest);
    f.position.set(x, h / 2, z);
    flames.push(f);
  }
  glowSprite(crest, '#bfe0ff', 0.5, V(0, 0.12, 0), 0.6);

  /* ---------- the shield arm: bent, the tower shield square before the chest ---------- */
  const armLB = bone(chest, V(-0.36, 1.58, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.37, 1.58, 0), V(-0.41, 1.24, 0.05), 0.085, 0.07, plate, 6);
  const foreLB = bone(aL, V(-0.41, 1.23, 0.06), 'foreL');
  const fL = foreLB.abs;
  k4.swell(fL, plateD, -0.41, 1.23, 0.06, 0.07, 0.07, 0.07, 6, 4);
  limb(fL, V(-0.41, 1.23, 0.06), V(-0.27, 1.18, 0.3), 0.068, 0.058, plate, 6);
  const shieldB = bone(fL, V(-0.25, 1.17, 0.34), 'shield');
  const sh = shieldB.abs;
  {
    const W = 0.6, H = 1.14;
    const shape = new THREE.Shape();
    shape.moveTo(-W / 2, -H / 2 + 0.05);
    shape.lineTo(-W / 2 + 0.06, -H / 2);
    shape.lineTo(W / 2 - 0.06, -H / 2);
    shape.lineTo(W / 2, -H / 2 + 0.05);
    shape.lineTo(W / 2, H / 2 - 0.1);
    shape.quadraticCurveTo(0, H / 2 + 0.08, -W / 2, H / 2 - 0.1);
    shape.closePath();
    const curve = (geo: THREE.BufferGeometry, k: number): THREE.BufferGeometry => {
      const pos = geo.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) pos.setZ(i, pos.getZ(i) - k * pos.getX(i) ** 2);
      geo.computeVertexNormals();
      return geo;
    };
    const face = new THREE.ExtrudeGeometry(shape, { depth: 0.04, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.012, bevelSegments: 1, curveSegments: 5 });
    // The face is cut into strips across so it can curve round the bearer.
    const sg = G(sh, -0.12, 0.98, 0.42);
    sg.rotation.set(-0.06, 0.12, 0);
    const faceM = M(curve(face, 0.9), paint, sg);
    faceM.position.z = -0.02;
    const rim = new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.03, bevelSegments: 1, curveSegments: 5 });
    const rimM = M(curve(rim, 0.9), plate, sg);
    rimM.position.z = -0.045;
    // A boss ridge down the middle, rivets, the Order's diamond cracked through with cold fire.
    const r = M(new THREE.BoxGeometry(0.04, H * 0.92, 0.03), plate, sg);
    r.position.set(0, -0.02, 0.05);
    const dia = M(new THREE.BoxGeometry(0.24, 0.24, 0.03), pale, sg);
    dia.position.set(0, 0.12, 0.065);
    dia.rotation.z = Math.PI / 4;
    const diaIn = M(new THREE.BoxGeometry(0.15, 0.15, 0.02), plateD, sg);
    diaIn.position.set(0, 0.12, 0.078);
    diaIn.rotation.z = Math.PI / 4;
    k4.crack(sg, [V(0.0, 0.28, 0.09), V(0.03, 0.17, 0.09), V(-0.02, 0.07, 0.09), V(0.02, -0.06, 0.08), V(-0.04, -0.2, 0.07), V(0.01, -0.34, 0.06)], 0.011, cold, 0.05);
    k4.crack(sg, [V(-0.02, 0.07, 0.09), V(-0.12, 0.02, 0.07), V(-0.2, -0.06, 0.04)], 0.008, cold, 0.04);
    glowSprite(sg, '#a9d4ff', 0.45, V(0, 0.1, 0.14), 0.5);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const x = Math.max(-1, Math.min(1, Math.cos(a) * 1.4)) * (W / 2 - 0.045);
      const y = Math.max(-1, Math.min(1, Math.sin(a) * 1.4)) * (H / 2 - 0.08);
      M(new THREE.SphereGeometry(0.016, 4, 3), pale, sg).position.set(x, y, 0.045 - 0.9 * x * x);
    }
    // A gouge across the lower half, and a notch hewn out of the rim.
    const gouge = M(new THREE.BoxGeometry(0.2, 0.018, 0.02), leatherD, sg);
    gouge.position.set(0.12, -0.28, 0.05);
    gouge.rotation.z = 0.5;
  }
  // The gauntlet on the grip behind it.
  M(new THREE.BoxGeometry(0.08, 0.09, 0.09), plateD, sh).position.set(-0.25, 1.17, 0.34);

  /* ---------- the sword arm: hanging, a broken longsword in the fist, point down ---------- */
  const armRB = bone(chest, V(0.36, 1.58, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.37, 1.58, 0), V(0.43, 1.23, 0.02), 0.085, 0.07, plate, 6);
  const foreRB = bone(aR, V(0.43, 1.22, 0.02), 'foreR');
  const fR = foreRB.abs;
  k4.swell(fR, plateD, 0.43, 1.22, 0.02, 0.07, 0.07, 0.07, 6, 4);
  limb(fR, V(0.43, 1.22, 0.02), V(0.45, 0.94, 0.1), 0.068, 0.06, plate, 6);
  const swordB = bone(fR, V(0.45, 0.9, 0.11), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.09, 0.1, 0.1), plateD, sw).position.set(0.45, 0.9, 0.11);
  {
    const s = G(sw, 0.45, 0.9, 0.11);
    s.rotation.x = Math.PI - 0.35;
    M(new THREE.CylinderGeometry(0.018, 0.018, 0.18, 5), leatherD, s).position.y = 0.0;
    M(new THREE.SphereGeometry(0.03, 5, 4), plate, s).position.y = -0.11;
    M(new THREE.BoxGeometry(0.26, 0.03, 0.045), plate, s).position.y = 0.1;
    M(new THREE.BoxGeometry(0.06, 0.44, 0.014), pale, s).position.y = 0.33;
    // The break: a jagged end, snapped off aslant.
    const tip = M(new THREE.ConeGeometry(0.035, 0.06, 3), pale, s);
    tip.position.set(0.012, 0.565, 0);
    tip.rotation.z = -0.5;
    tip.scale.z = 0.3;
    const chip = M(new THREE.ConeGeometry(0.02, 0.05, 3), pale, s);
    chip.position.set(-0.018, 0.56, 0);
    chip.rotation.z = 0.3;
    chip.scale.z = 0.3;
  }

  /* ---------- a cold black mist breathing off him (the frames boil it) ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.3, -0.18, 0.55, 1.5], [-0.24, -0.2, 0.5, 1.45]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.04, smokeM, rng, 0.05));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    flames.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.45 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, SENTINEL_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
